
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface SiteColors {
  primary: string;
  primary_light: string;
  primary_dark: string;
  secondary: string;
}

// قراءة الألوان من CSS variables التي تم تعيينها في index.html
const getInitialColors = (): SiteColors => {
  if (typeof window !== 'undefined') {
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);
    return {
      primary: computedStyle.getPropertyValue('--color-primary').trim() || '#5D4037',
      primary_light: computedStyle.getPropertyValue('--color-primary-light').trim() || '#8D6E63',
      primary_dark: computedStyle.getPropertyValue('--color-primary-dark').trim() || '#3E2723',
      secondary: computedStyle.getPropertyValue('--color-secondary').trim() || '#EFEBE9',
    };
  }
  // Fallback for server‑side rendering
  return {
    primary: '#5D4037',
    primary_light: '#8D6E63',
    primary_dark: '#3E2723',
    secondary: '#EFEBE9',
  };
};

export const useSiteColors = () => {
  const [colors, setColors] = useState<SiteColors>(getInitialColors);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load initial colors from the database
    loadColors();

    // Listen for real‑time changes
    const subscription = supabase
      .channel('site_colors_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'site_settings',
          filter: 'setting_key=eq.site_colors',
        },
        (payload) => {
          if (payload.new && (payload.new as any).setting_value) {
            const newColors = (payload.new as any).setting_value as SiteColors;
            setColors(newColors);
            applyColors(newColors);
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const loadColors = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .eq('setting_key', 'site_colors')
        .maybeSingle();

      if (error) {
        // Propagate the error to the catch block
        throw error;
      }

      if (data?.setting_value) {
        const newColors = data.setting_value as SiteColors;
        setColors(newColors);
        applyColors(newColors);
      }
    } catch (error) {
      console.error('Error loading colors:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyColors = (newColors: SiteColors) => {
    try {
      const root = document.documentElement;
      root.style.setProperty('--color-primary', newColors.primary);
      root.style.setProperty('--color-primary-light', newColors.primary_light);
      root.style.setProperty('--color-primary-dark', newColors.primary_dark);
      root.style.setProperty('--color-secondary', newColors.secondary);
    } catch (e) {
      console.error('Failed to apply site colors:', e);
    }
  };

  return { colors, loading };
};
